import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { fetchIFRSAccounts } from '../../services/invoicingService';
import ListLoadFailed from '../ui/ListLoadFailed';

/**
 * Type-ahead picker for the standardized IFRS reference chart.
 *
 * The number field is a combobox: typing a code prefix ("3.1.1") or words
 * ("payable") searches the reference table and shows suggestions; picking one
 * fills BOTH the number and the title so they can't drift apart. Both fields
 * remain free text, so a custom sub-code that isn't in the standard chart can
 * still be typed by hand.
 *
 * Props:
 *   number, title  -- current values (controlled by the parent form)
 *   onChange({ ifrs_account_number, ifrs_account_title }) -- always both fields
 */
const IFRSAccountPicker = ({ number = '', title = '', onChange }) => {
  const [query, setQuery] = useState(number);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchAttempt, setSearchAttempt] = useState(0);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const seqRef = useRef(0);

  // Sync the input when the parent resets the form (modal open / edit / clear).
  useEffect(() => { setQuery(number); }, [number]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) { setResults([]); setLoading(false); return undefined; }
    const seq = ++seqRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await fetchIFRSAccounts(q, { limit: 20 });
        if (seq === seqRef.current) { setResults(data); setSearchFailed(false); setLoading(false); }
      } catch (err) {
        // Row 128: a failed search must not read as "no match — custom code".
        if (seq === seqRef.current) { setResults([]); setSearchFailed(true); setLoading(false); }
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, searchAttempt]);

  const emit = (num, ttl) => onChange && onChange({ ifrs_account_number: num, ifrs_account_title: ttl });

  const handleNumberChange = (val) => {
    setQuery(val);
    setOpen(true);
    setHighlight(-1);
    emit(val, title);
  };

  const pick = (item) => {
    setQuery(item.account_number);
    setResults([]);
    setOpen(false);
    setHighlight(-1);
    emit(item.account_number, item.account_title);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const isCustom = query.trim() && !loading && open && results.length === 0 && !searchFailed;

  return (
    <>
      <div className="md-form-group" ref={wrapRef} style={{ position: 'relative' }}>
        <label className="md-form-label">IFRS Account Number</label>
        <div style={{ position: 'relative' }}>
          <input
            className="md-form-input"
            type="text"
            value={query}
            onChange={(e) => handleNumberChange(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search code or name, e.g. 3.1.1 or payable"
            autoComplete="off"
          />
          <FontAwesomeIcon
            icon={loading ? faSpinner : faSearch}
            spin={loading}
            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
          />
        </div>
        {open && searchFailed && query.trim() && !loading && (
          <div style={{ marginTop: '4px' }}>
            <ListLoadFailed what="the chart of accounts" onRetry={() => setSearchAttempt((n) => n + 1)} />
          </div>
        )}
        {open && (results.length > 0 || isCustom) && (
          <div style={{
            position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: '4px',
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: '240px', overflowY: 'auto',
          }}>
            {results.map((item, i) => (
              <div
                key={item.id}
                onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: '8px 12px', cursor: 'pointer',
                  background: highlight === i ? '#eff6ff' : 'transparent',
                  borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <span style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#2563eb' }}>{item.account_number}</span>
                <span style={{ margin: '0 6px', color: '#111827' }}>{item.account_title}</span>
                {item.balance && <span style={{ fontSize: '0.8em', color: '#9ca3af' }}>{item.balance}</span>}
              </div>
            ))}
            {isCustom && (
              <div style={{ padding: '8px 12px', fontSize: '0.85em', color: '#9ca3af' }}>
                No match in the standard chart — "{query.trim()}" will be saved as a custom code.
              </div>
            )}
          </div>
        )}
      </div>
      <div className="md-form-group">
        <label className="md-form-label">IFRS Account Title</label>
        <input
          className="md-form-input"
          type="text"
          value={title}
          onChange={(e) => emit(query, e.target.value)}
          placeholder="e.g. Trade accounts payable"
        />
      </div>
    </>
  );
};

export default IFRSAccountPicker;
